/**
 * DraftKings Sportsbook betslip deeplinks.
 *
 * Free-to-play games (NFL prop-based Daily Reveal / "Gridiron Draw", and any
 * future prop game) can hand a player's submitted outcomes straight to the DK
 * Sportsbook app so those selections land in their betslip.
 *
 * The contract, as confirmed by DraftKings:
 *
 *   dksb://sb/page/{cmsPageId}?outcomes={selectionId},{selectionId}
 *
 * Two separate identifiers are involved and they must not be confused:
 *
 *  1. The CMS page id decides WHICH sportsbook page opens. It is per-sport, so
 *     it is selected from the contest's league (`pickems.sport`) — never from the
 *     Daily Reveal game type, which describes the mechanic and not the sport.
 *  2. The `outcomes` values are sportsbook SELECTION ids (`selection.id` from the
 *     Sportsbook Partners API), persisted as
 *     `daily_reveal_prop_markets.sportsbook_selection_id` when a market is frozen.
 *     The sportsbook MARKET id plays no part in the deeplink.
 *
 * Only NFL has been supplied by DK so far. Sports without a mapping deliberately
 * produce no deeplink rather than falling back to the NFL page.
 */

import type { SportCode } from './leagues';

/** Custom URL scheme registered by the DraftKings Sportsbook app. */
export const SPORTSBOOK_DEEPLINK_SCHEME = 'dksb';

/**
 * Web sportsbook host, used when nothing on the device claims `dksb://`.
 *
 * A custom scheme only resolves if the DK Sportsbook app is installed — that is an
 * OS rule, not something a correct scheme can overcome — so a player without the app
 * taps into nothing.
 */
export const SPORTSBOOK_WEB_BASE_URL = 'https://sportsbook.draftkings.com';

/** Query parameter carrying the comma-separated sportsbook selection ids. */
export const SPORTSBOOK_BETSLIP_OUTCOMES_PARAM = 'outcomes';

/**
 * Which DraftKings Sportsbook estate a deeplink is aimed at.
 *
 * The two are NOT the same URL with a different host — DK supplied genuinely
 * different path shapes for each, so the difference is the prefix AND the per-sport
 * segment together. Hence a target bundles both rather than exposing a host to swap.
 */
export type SportsbookBetslipTargetName = 'production' | 'staging';

export type SportsbookBetslipTarget = {
  /** Everything before the per-sport segment, e.g. `dksb://sb/page`. */
  appPagePrefix: string;
  /**
   * Per-sport path segment. Values are supplied by DraftKings and must never be
   * guessed — an invented page id sends players to the wrong sport.
   */
  pageBySport: Partial<Record<SportCode, string>>;
};

export const SPORTSBOOK_BETSLIP_TARGETS: Record<
  SportsbookBetslipTargetName,
  SportsbookBetslipTarget
> = {
  /**
   * Production, and the default for every environment that is not explicitly
   * staging. These are the values DK confirmed for the live app and the ones we
   * keep pointing at prod builds.
   *
   *   dksb://sb/page/leagues/football/nfl/88808?outcomes=…
   */
  production: {
    appPagePrefix: `${SPORTSBOOK_DEEPLINK_SCHEME}://sb/page`,
    pageBySport: {
      NFL: 'leagues/football/nfl/88808',
      // NBA / MLB / NCAAMB / … : awaiting page ids from DraftKings.
    },
  },
  /**
   * Preprod, supplied by DK after the CMS-page form failed to resolve on stage:
   *
   *   dksb://sb/league/3/88808?outcomes=…
   *
   * `88808` is unchanged from the production path — only the route to it differs
   * (`sb/league/3/…` rather than `sb/page/leagues/football/nfl/…`), which is why
   * `3` is stored as part of the sport's segment rather than modelled as a
   * separate sport id. DK have not told us what `3` denotes, so nothing here
   * derives it; if a second sport ever gets a preprod page we will need them to
   * supply its full segment too.
   */
  staging: {
    appPagePrefix: `${SPORTSBOOK_DEEPLINK_SCHEME}://sb/league`,
    pageBySport: {
      NFL: '3/88808',
    },
  },
};

/** Default target: prod links everywhere unless a caller explicitly asks for staging. */
export const DEFAULT_SPORTSBOOK_BETSLIP_TARGET: SportsbookBetslipTargetName = 'production';

/** Everything before the CMS page id on production, e.g. `dksb://sb/page`. */
export const SPORTSBOOK_DEEPLINK_PAGE_PREFIX = SPORTSBOOK_BETSLIP_TARGETS.production.appPagePrefix;

/** Production per-sport CMS pages. */
export const SPORTSBOOK_PAGE_BY_SPORT = SPORTSBOOK_BETSLIP_TARGETS.production.pageBySport;

/** Path segment for a contest's sport code, or null when DK has supplied none. */
export function getSportsbookPageForSport(
  sport: string | null | undefined,
  target: SportsbookBetslipTargetName = DEFAULT_SPORTSBOOK_BETSLIP_TARGET
): string | null {
  if (!sport) return null;
  const code = sport.trim().toUpperCase() as SportCode;
  return SPORTSBOOK_BETSLIP_TARGETS[target]?.pageBySport[code] ?? null;
}

/** Whether a betslip deeplink can be built for this sport at all. */
export function isSportsbookBetslipSupportedSport(
  sport: string | null | undefined,
  target: SportsbookBetslipTargetName = DEFAULT_SPORTSBOOK_BETSLIP_TARGET
): boolean {
  return getSportsbookPageForSport(sport, target) !== null;
}

/**
 * Clean a list of sportsbook selection ids for use as `outcomes`: trims, drops
 * null/undefined/blank entries and de-duplicates while preserving first-seen
 * order (the order the player's selections were made in).
 *
 * Ids stay strings throughout — they are external identifiers and may one day
 * exceed JavaScript's safe integer range.
 */
export function normalizeSportsbookSelectionIds(
  selectionIds: readonly (string | null | undefined)[] | null | undefined
): string[] {
  if (!selectionIds) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of selectionIds) {
    if (typeof raw !== 'string') continue;
    const id = raw.trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    result.push(id);
  }
  return result;
}

/**
 * Build the DK Sportsbook betslip deeplink for a contest's sport and the
 * sportsbook selection ids of the outcomes the player actually submitted.
 *
 * Returns null — meaning "no actionable betslip", so callers hide the CTA —
 * when the sport has no DK page or when no usable selection id remains. It
 * never emits a bare `?outcomes=` and never falls back to another sport.
 */
export function buildSportsbookBetslipDeeplink(
  sport: string | null | undefined,
  selectionIds: readonly (string | null | undefined)[] | null | undefined,
  target: SportsbookBetslipTargetName = DEFAULT_SPORTSBOOK_BETSLIP_TARGET
): string | null {
  const config = SPORTSBOOK_BETSLIP_TARGETS[target] ?? SPORTSBOOK_BETSLIP_TARGETS.production;
  return buildBetslipUrl(config.appPagePrefix, getSportsbookPageForSport(sport, target), selectionIds);
}

/**
 * https equivalent of {@link buildSportsbookBetslipDeeplink}, for devices where the
 * DK Sportsbook app is not installed to claim the `dksb://` scheme.
 *
 * Deliberately NOT per-target: this is the one form of the link confirmed working
 * end to end (a browser tap opens the sportsbook with the selection loaded), and the
 * preprod app path would not address anything on the web host — `…/3/88808` is a
 * route into the app, not a page on sportsbook.draftkings.com. So the web fallback
 * always uses the production page, on every environment.
 *
 * Returns null under the same sport/selection conditions as the production app
 * deeplink, so the two never disagree about whether a handoff is possible.
 */
export function buildSportsbookBetslipWebUrl(
  sport: string | null | undefined,
  selectionIds: readonly (string | null | undefined)[] | null | undefined
): string | null {
  return buildBetslipUrl(
    SPORTSBOOK_WEB_BASE_URL,
    getSportsbookPageForSport(sport, 'production'),
    selectionIds
  );
}

function buildBetslipUrl(
  prefix: string,
  page: string | null,
  selectionIds: readonly (string | null | undefined)[] | null | undefined
): string | null {
  if (!page) return null;

  const outcomes = normalizeSportsbookSelectionIds(selectionIds);
  if (outcomes.length === 0) return null;

  const encoded = outcomes.map(id => encodeURIComponent(id)).join(',');
  return `${prefix}/${page}?${SPORTSBOOK_BETSLIP_OUTCOMES_PARAM}=${encoded}`;
}
